<!-- Content Header (Page header) -->
<section class="content-header">
    <div class="container-fluid">
        <div class="row mb-2">
            <div class="col-sm-6">
                <h1>Collects AI Analytics</h1>
            </div>
            <div class="col-sm-6">
                <ol class="breadcrumb float-sm-right">
                    <li class="breadcrumb-item"><a routerLink="/">Home</a></li>
                    <li class="breadcrumb-item active">Collects AI Analytics</li>
                </ol>
            </div>
        </div>
    </div>
</section>

<!-- Main content -->
<section class="content">
    <div class="container-fluid analytics-container">
        <!-- Loading Spinner -->
        <div class="loading-shade" *ngIf="isLoading">
            <mat-spinner></mat-spinner>
        </div>

        <!-- Filter Card -->
        <mat-card class="filter-card">
            <mat-card-header>
                <mat-card-title>Filters</mat-card-title>
            </mat-card-header>
            <mat-card-content>
                <div class="filter-form">
                    <!-- Organisation Selector -->
                    <mat-form-field appearance="outline" class="org-field">
                        <mat-label>Organisation</mat-label>
                        <mat-select [formControl]="selectedOrg">
                            <mat-option *ngFor="let org of organisations" [value]="org.tag">
                                {{ org.name }}
                            </mat-option>
                        </mat-select>
                        <mat-spinner
                            *ngIf="isLoadingOrgs"
                            matSuffix
                            diameter="20"
                        ></mat-spinner>
                    </mat-form-field>

                    <!-- Date Range -->
                    <mat-form-field appearance="outline" class="date-field">
                        <mat-label>Date Range (optional)</mat-label>
                        <mat-date-range-input [formGroup]="dateRange" [rangePicker]="picker">
                            <input
                                matStartDate
                                formControlName="start"
                                placeholder="Start date"
                            />
                            <input
                                matEndDate
                                formControlName="end"
                                placeholder="End date"
                            />
                        </mat-date-range-input>
                        <mat-datepicker-toggle
                            matSuffix
                            [for]="picker"
                        ></mat-datepicker-toggle>
                        <mat-date-range-picker #picker></mat-date-range-picker>
                    </mat-form-field>

                    <!-- Action Buttons -->
                    <div class="action-buttons">
                        <button
                            mat-raised-button
                            color="primary"
                            (click)="fetchAnalytics()"
                            [disabled]="!selectedOrg.value || isLoading"
                        >
                            <mat-icon>search</mat-icon> Fetch Analytics
                        </button>
                        <button mat-raised-button (click)="resetFilters()">
                            <mat-icon>clear</mat-icon> Reset
                        </button>
                    </div>
                </div>
            </mat-card-content>
        </mat-card>

        <!-- Empty State -->
        <div class="empty-state" *ngIf="!dataLoaded && !isLoading">
            <mat-icon>analytics</mat-icon>
            <p>Select an organisation and click "Fetch Analytics" to view data</p>
        </div>

        <!-- Key Metrics Cards -->
        <div class="metrics-grid" *ngIf="dataLoaded && usageData">
            <mat-card class="stat-card">
                <mat-card-content>
                    <div class="stat-icon">
                        <mat-icon color="primary">timeline</mat-icon>
                    </div>
                    <div class="stat-content">
                        <div class="stat-value">
                            {{ usageData.total_requests | number }}
                        </div>
                        <div class="stat-label">Total Requests</div>
                    </div>
                </mat-card-content>
            </mat-card>

            <mat-card class="stat-card">
                <mat-card-content>
                    <div class="stat-icon">
                        <mat-icon color="accent">people</mat-icon>
                    </div>
                    <div class="stat-content">
                        <div class="stat-value">
                            {{ usageData.unique_sessions | number }}
                        </div>
                        <div class="stat-label">Unique Sessions</div>
                    </div>
                </mat-card-content>
            </mat-card>

            <mat-card class="stat-card" *ngIf="sessionData">
                <mat-card-content>
                    <div class="stat-icon">
                        <mat-icon color="primary">chat</mat-icon>
                    </div>
                    <div class="stat-content">
                        <div class="stat-value">
                            {{ sessionData.avg_messages_per_session | number : '1.1-1' }}
                        </div>
                        <div class="stat-label">Avg Messages/Session</div>
                    </div>
                </mat-card-content>
            </mat-card>

            <mat-card class="stat-card" *ngIf="performanceData">
                <mat-card-content>
                    <div class="stat-icon">
                        <mat-icon color="accent">speed</mat-icon>
                    </div>
                    <div class="stat-content">
                        <div class="stat-value">
                            {{ performanceData.avg_response_time_ms | number }}ms
                        </div>
                        <div class="stat-label">Avg Response Time</div>
                    </div>
                </mat-card-content>
            </mat-card>

            <mat-card class="stat-card" *ngIf="feedbackData">
                <mat-card-content>
                    <div class="stat-icon">
                        <mat-icon [style.color]="feedbackData.satisfaction_rate >= 0.8 ? '#4caf50' : feedbackData.satisfaction_rate >= 0.5 ? '#ff9800' : '#f44336'">
                            thumb_up
                        </mat-icon>
                    </div>
                    <div class="stat-content">
                        <div class="stat-value">
                            {{ feedbackData.satisfaction_rate * 100 | number : '1.0-0' }}%
                        </div>
                        <div class="stat-label">Satisfaction Rate</div>
                    </div>
                </mat-card-content>
            </mat-card>
        </div>

        <!-- Charts Grid -->
        <div class="charts-grid" *ngIf="dataLoaded">
            <!-- Usage Trends Line Chart -->
            <mat-card class="chart-card full-width">
                <mat-card-header>
                    <mat-card-title>Usage Trends Over Time</mat-card-title>
                    <mat-card-subtitle
                        >Daily requests and sessions breakdown</mat-card-subtitle
                    >
                </mat-card-header>
                <mat-card-content>
                    <div class="chart-container">
                        <canvas
                            baseChart
                            *ngIf="usageLineChartData.length > 0"
                            [datasets]="usageLineChartData"
                            [labels]="usageLineChartLabels"
                            [options]="usageLineChartOptions"
                            [colors]="usageLineChartColors"
                            [chartType]="usageLineChartType"
                            [legend]="true"
                        >
                        </canvas>
                        <div
                            class="no-data"
                            *ngIf="usageLineChartData.length === 0"
                        >
                            No usage data available
                        </div>
                    </div>
                </mat-card-content>
            </mat-card>

            <!-- Session Length Distribution Pie Chart -->
            <mat-card class="chart-card">
                <mat-card-header>
                    <mat-card-title>Session Length Distribution</mat-card-title>
                    <mat-card-subtitle
                        >Sessions grouped by message count</mat-card-subtitle
                    >
                </mat-card-header>
                <mat-card-content>
                    <div class="chart-container">
                        <canvas
                            baseChart
                            *ngIf="sessionPieChartData.length > 0"
                            [data]="sessionPieChartData"
                            [labels]="sessionPieChartLabels"
                            [chartType]="sessionPieChartType"
                            [options]="sessionPieChartOptions"
                            [colors]="sessionPieChartColors"
                            [legend]="true"
                        >
                        </canvas>
                        <div
                            class="no-data"
                            *ngIf="sessionPieChartData.length === 0"
                        >
                            No session data available
                        </div>
                    </div>
                </mat-card-content>
            </mat-card>

            <!-- Performance by Agent Bar Chart -->
            <mat-card class="chart-card">
                <mat-card-header>
                    <mat-card-title>Performance by Agent</mat-card-title>
                    <mat-card-subtitle
                        >Average response time comparison</mat-card-subtitle
                    >
                </mat-card-header>
                <mat-card-content>
                    <div class="chart-container">
                        <canvas
                            baseChart
                            *ngIf="performanceBarChartData.length > 0"
                            [datasets]="performanceBarChartData"
                            [labels]="performanceBarChartLabels"
                            [options]="performanceBarChartOptions"
                            [colors]="performanceBarChartColors"
                            [chartType]="performanceBarChartType"
                            [legend]="true"
                        >
                        </canvas>
                        <div
                            class="no-data"
                            *ngIf="performanceBarChartData.length === 0"
                        >
                            No performance data available
                        </div>
                    </div>
                </mat-card-content>
            </mat-card>

            <!-- Performance Percentiles Card -->
            <mat-card class="metrics-detail-card" *ngIf="performanceData">
                <mat-card-header>
                    <mat-card-title>Response Time Percentiles</mat-card-title>
                </mat-card-header>
                <mat-card-content>
                    <div class="percentile-list">
                        <div class="percentile-item">
                            <span class="percentile-label">P50 (Median):</span>
                            <span class="percentile-value"
                                >{{ performanceData.p50_response_time_ms | number }}ms</span
                            >
                        </div>
                        <div class="percentile-item">
                            <span class="percentile-label">P95:</span>
                            <span class="percentile-value"
                                >{{ performanceData.p95_response_time_ms | number }}ms</span
                            >
                        </div>
                        <div class="percentile-item">
                            <span class="percentile-label">Average:</span>
                            <span class="percentile-value"
                                >{{ performanceData.avg_response_time_ms | number }}ms</span
                            >
                        </div>
                    </div>
                </mat-card-content>
            </mat-card>

            <!-- Session Details Card -->
            <mat-card class="metrics-detail-card" *ngIf="sessionData">
                <mat-card-header>
                    <mat-card-title>Session Details</mat-card-title>
                </mat-card-header>
                <mat-card-content>
                    <div class="detail-list">
                        <div class="detail-item">
                            <span class="detail-label">Total Sessions:</span>
                            <span class="detail-value">{{
                                sessionData.total_sessions | number
                            }}</span>
                        </div>
                        <div class="detail-item">
                            <span class="detail-label">Avg Messages:</span>
                            <span class="detail-value">{{
                                sessionData.avg_messages_per_session | number : '1.1-1'
                            }}</span>
                        </div>
                    </div>
                </mat-card-content>
            </mat-card>

            <!-- Feedback by Agent Chart -->
            <mat-card class="chart-card">
                <mat-card-header>
                    <mat-card-title>Feedback by Agent</mat-card-title>
                    <mat-card-subtitle
                        >Positive and negative feedback distribution</mat-card-subtitle
                    >
                </mat-card-header>
                <mat-card-content>
                    <div class="chart-container">
                        <canvas
                            baseChart
                            *ngIf="feedbackBarChartData.length > 0"
                            [datasets]="feedbackBarChartData"
                            [labels]="feedbackBarChartLabels"
                            [options]="feedbackBarChartOptions"
                            [colors]="feedbackBarChartColors"
                            [chartType]="feedbackBarChartType"
                            [legend]="true"
                        >
                        </canvas>
                        <div
                            class="no-data"
                            *ngIf="feedbackBarChartData.length === 0"
                        >
                            No feedback data available
                        </div>
                    </div>
                </mat-card-content>
            </mat-card>

            <!-- Feedback Details Card -->
            <mat-card class="metrics-detail-card" *ngIf="feedbackData">
                <mat-card-header>
                    <mat-card-title>Feedback Summary</mat-card-title>
                </mat-card-header>
                <mat-card-content>
                    <div class="detail-list">
                        <div class="detail-item">
                            <span class="detail-label">Total Feedback:</span>
                            <span class="detail-value">{{
                                feedbackData.total_feedback_count | number
                            }}</span>
                        </div>
                        <div class="detail-item positive">
                            <span class="detail-label">Positive:</span>
                            <span class="detail-value">{{
                                feedbackData.positive_count | number
                            }}</span>
                        </div>
                        <div class="detail-item negative">
                            <span class="detail-label">Negative:</span>
                            <span class="detail-value">{{
                                feedbackData.negative_count | number
                            }}</span>
                        </div>
                    </div>
                </mat-card-content>
            </mat-card>
        </div>
    </div>
</section>
